import React, { FC, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { GenericObject } from '@dxb-web-ui-framework/common/types';
import { Grid, MenuMore } from '@dxb-web-ui-framework/design-system';
import { MoreHoriz as MoreIcon, GetApp } from '@dxb-web-ui-framework/design-system/dist/icons';
import { useTranslation } from 'react-i18next';
import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks';
import { Helmet } from 'react-helmet';
import { fundTransfersService } from '../../../../channels/secure-web/services';
import { ExchangeCalculateResponseBody } from '../../../fundTransfer/src/domain/Transfer/entities/ExchangeCalculateResponseDTO';
import { LayoutBuilder } from '../../../content-composer/src/components/Layouts';
import { TransactionDetailsContent } from '../../../transaction/src/components/TransactionDetails/TransactionDetailsDrawer';
import {
  useAuthorizationLoadingState,
  useAuthorizationErrorState,
  ApproveRejectAuthFlowPresenter,
  useOnTransactionDetailsMutation,
  useAuthorizationSelectAction,
  useAuthorizationSelectedAuthorization,
  useAuthorizationTransactionDetails,
  useCheckAuthorization,
  useOnRefetchPendingAuthizationsListMutation,
  useNotificationsLoadingState,
  useNotificationsErrorState,
  useOnSetAuthAction,
  useAuthAction,
  useOnShouldShowRequestNewRateBtn,
  useOnToggleAuthFlowDrawer,
} from '..';
import { MobileHeader } from '../../../content-composer/src/components/Partials/Header';
import { Mandate } from '../domain/Authorization/entities';
import { ErrorContent } from '../../../content-composer/src/components/Partials/ErrorContent';
import { exchangeAmountPredicator } from '../../../transaction/src/utils';
import { ConsolidatedTransactionDetails } from '../../../transaction/src/domain/Transaction/entities/ConsolidatedTransaction';
import { useDownloadTransactionDetails } from '../../../content-composer/src/hooks/useDownloadTransactionDetails';
import { usePrintSwiftConfirmation } from '../../../content-composer/src/hooks/usePrintSwiftConfirmation';
import { useSelectedCustomerId } from '../../../userProfile/src/contexts/UserInfo/hooks';
import { getRoutePath } from '../../../content-composer/src/components/Routes/routePaths';

// TODO: add page header with action menu
export type AuthorizationDetailsPageProps = {
  pageConfig: GenericObject;
};

const AuthorizationDetailsPage: FC<AuthorizationDetailsPageProps> = ({ pageConfig }) => {
  const authAction = useAuthAction();
  const setAuthAction = useOnSetAuthAction();
  const selectedAuthorization = useAuthorizationSelectedAuthorization();
  const selectedAuthorizationTransactionDetails = useAuthorizationTransactionDetails();
  const onSelectAuthorization = useAuthorizationSelectAction();
  const onTransactionDetailsMutation = useOnTransactionDetailsMutation();
  const onAuthListMutation = useOnRefetchPendingAuthizationsListMutation();
  const isLoadingAuthorizationList = useNotificationsLoadingState('isLoadingPendingAuthorizations');
  const isLoadingTransactionDetails = useAuthorizationLoadingState('isLoadingTransactionDetails');
  const transactionDetailsError = useAuthorizationErrorState('transactionDetailsError');
  const transactionListError = useNotificationsErrorState('pendingAuthorizationsListError');
  const isLoadingCheckAuthorization = useAuthorizationLoadingState('isLoadingCheckAuthorization');
  const checkAuthorizationData = useCheckAuthorization();
  const shouldShowRequestNewRateBtn = useOnShouldShowRequestNewRateBtn();
  const onToggleAuthFlowDrawer = useOnToggleAuthFlowDrawer();

  const { t } = useTranslation();
  const navigate = useNavigate();
  const customerId = useSelectedCustomerId();

  const { isLoadingDownloadData, onDownloadDetails } = useDownloadTransactionDetails();
  const { isLoadingSwiftConfirmationDownloadData, onDownloadSwiftConfirmationDetails } = usePrintSwiftConfirmation();
  const isSwiftDetailsDownloadEnabled =
    selectedAuthorizationTransactionDetails &&
    selectedAuthorizationTransactionDetails?.orderProcessType === 'SWIFT' &&
    selectedAuthorizationTransactionDetails?.status === '3';

  const mobileHeaderActions = [
    {
      Component: MenuMore,
      props: {
        icon: <MoreIcon />,
        menuTitle: t('accountDetailsPage.AccountExtraitTransactionDetailsPage.MobileHeaderActions.MenuTitle'),
        drawerTitle: t('accountDetailsPage.AccountExtraitTransactionDetailsPage.MobileHeaderActions.DrawerTitle'),
        items: [
          {
            icon: <GetApp />,
            label: t(
              'accountDetailsPage.AccountExtraitTransactionDetailsPage.MobileHeaderActions.DownloadDetailsLabel',
            ),
            disabled: isLoadingDownloadData || !selectedAuthorization?.globalOrderId,
            onClick: () =>
              onDownloadDetails(
                String(selectedAuthorization?.globalOrderId) as string,
                selectedAuthorization?.accountNumber as string,
              ),
            title: t(
              'accountDetailsPage.AccountExtraitTransactionDetailsPage.MobileHeaderActions.DownloadDetailsTitle',
            ),
          },
          {
            disabled: !isSwiftDetailsDownloadEnabled || isLoadingSwiftConfirmationDownloadData,
            icon: <GetApp />,
            label: t('TransactionDetailsDrawerFileDownload.SwiftDetails'),
            onClick: () =>
              onDownloadSwiftConfirmationDetails(
                String(selectedAuthorizationTransactionDetails?.orderId) as string,
                selectedAuthorizationTransactionDetails?.fromAccount as string,
              ),
          },
        ],
      },
    },
  ];

  const actionCallback = useCallback(
    (event: any, name: string, transactionNo: string, isActionFromDetailsDrawer: boolean) => {
      switch (name) {
        case 'approve':
          onSelectAuthorization(transactionNo);
          setAuthAction(name);
          break;
        case 'reject':
          onSelectAuthorization(transactionNo);
          setAuthAction(name);
          break;
        default:
          return;
      }
    },
    [onSelectAuthorization, setAuthAction],
  );

  const doesUserAwaitsForDealerRateAcceptance = Boolean(
    selectedAuthorizationTransactionDetails &&
      selectedAuthorizationTransactionDetails.processingStatus === '6' &&
      !selectedAuthorizationTransactionDetails.dealRefNo &&
      !selectedAuthorizationTransactionDetails.dealUsrCode,
  );

  const shouldMakeExchangeAmountCall =
    !!selectedAuthorizationTransactionDetails &&
    ((exchangeAmountPredicator(selectedAuthorizationTransactionDetails?.processingStatus) &&
      (selectedAuthorizationTransactionDetails?.fromCurrencyId !==
        selectedAuthorizationTransactionDetails?.toCurrencyId ||
        (selectedAuthorizationTransactionDetails?.internalDestinationCurrencyId !== 0 &&
          selectedAuthorizationTransactionDetails?.internalDestinationCurrencyId !==
            selectedAuthorizationTransactionDetails?.fromCurrencyId))) ||
      doesUserAwaitsForDealerRateAcceptance);

  const {
    data: exchangeCalculateData,
    error: exchangeCalculateError,
    isLoading: isLoadingExchangeCalculate,
  } = useInvokeSWRService<ExchangeCalculateResponseBody>(
    fundTransfersService.ExchangeCalculate,
    {
      fromCurrencyId: selectedAuthorizationTransactionDetails?.fromCurrencyId,
      toCurrencyId:
        selectedAuthorizationTransactionDetails?.internalDestinationCurrencyId !== 0 &&
        selectedAuthorizationTransactionDetails?.internalDestinationCurrencyId !==
          selectedAuthorizationTransactionDetails?.fromCurrencyId
          ? selectedAuthorizationTransactionDetails?.internalDestinationCurrencyId
          : selectedAuthorizationTransactionDetails?.toCurrencyId,
      amount: selectedAuthorizationTransactionDetails?.amount,
      isSideTypeBuy:
        selectedAuthorizationTransactionDetails?.accountCurrency ===
        selectedAuthorizationTransactionDetails?.exchangeCurrency,
    },
    shouldMakeExchangeAmountCall,
    'fundTransfersService.ExchangeCalculate',
  );

  const error = transactionDetailsError || transactionListError || exchangeCalculateError;
  const isLoading =
    isLoadingTransactionDetails ||
    isLoadingCheckAuthorization ||
    isLoadingAuthorizationList ||
    isLoadingExchangeCalculate;

  const mutatorHandler = () => {
    if (transactionDetailsError || exchangeCalculateError) {
      onTransactionDetailsMutation();
      return;
    }
    if (transactionListError) {
      onAuthListMutation();
    }
  };

  const shouldRenderFlow = Boolean(authAction) && Boolean(authAction === 'reject' || authAction === 'approve');

  const onBackOverride = useCallback(() => {
    navigate(getRoutePath({ routeKey: 'authorizations', customerId }));
  }, [navigate, customerId]);

  return (
    <>
      <Helmet>
        <title>{t(pageConfig.title)}</title>
      </Helmet>

      <MobileHeader actions={mobileHeaderActions} onBackOverride={onBackOverride} />

      <LayoutBuilder layout={!shouldRenderFlow ? pageConfig.layout : { ...pageConfig.layout, label: undefined }}>
        <Grid item xs={12}>
          {error && !isLoading && (
            <ErrorContent
              code={error?.code || '404'}
              onClickBtn={mutatorHandler}
              subtitle={error?.message}
              btnLabel={t('authorizationsListPage.ApproveRejectFlow.ErrorContent.BtnLabel')}
              message={t('authorizationsListPage.ApproveRejectFlow.ErrorContent.Message')}
            />
          )}
          {!shouldRenderFlow && !error && (
            <TransactionDetailsContent
              variant={'page'}
              isLoading={isLoading}
              doesUserAwaitsForDealerRateAcceptance={doesUserAwaitsForDealerRateAcceptance}
              transaction={selectedAuthorization as Mandate}
              transactionDetails={selectedAuthorizationTransactionDetails as ConsolidatedTransactionDetails}
              isAuthorization={true}
              actionCallback={actionCallback}
              shouldFetchDealerRates={true}
              needMandate={checkAuthorizationData?.needMandate ?? true}
              lastAuthorizer={checkAuthorizationData?.lastAuthorizer ?? false}
              exchangeCalculateData={exchangeCalculateData}
              shouldShowMandateRequestRateButton={shouldShowRequestNewRateBtn}
              shouldShowApproximateRate={true}
            />
          )}
          <ApproveRejectAuthFlowPresenter authAction={authAction} onToggleDrawer={onToggleAuthFlowDrawer} />
        </Grid>
      </LayoutBuilder>
    </>
  );
};

export { AuthorizationDetailsPage };

export default AuthorizationDetailsPage;
